import React, { useState, useEffect, useMemo, forwardRef } from 'react'
import { Button, Modal } from "react-bootstrap";
import MaterialTable from '@material-table/core'
import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Print from '@material-ui/icons/Print';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import RemoveRedEye from '@material-ui/icons/RemoveRedEye';
import Message from '@material-ui/icons/Message';
import swal from 'sweetalert';
import Cookies from 'universal-cookie/es6';
import Navbar2 from './Navbar'

const api = process.env.REACT_APP_API;

// ─── helpers ──────────────────────────────────────────────────────────────────
const DAYS_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const DAYS_FULL  = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"];
const MONTHS     = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MONTHS_SHORT = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const CAL_GRID_LINE = "#c7d2e0";

const pad  = (n) => String(n).padStart(2, "0");
const ymd  = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const sameDay = (a, b) => ymd(a) === ymd(b);

const toMinutes = (value) => {
  if (!value || typeof value !== "string") return null;
  const [hourRaw, minuteRaw] = value.slice(0, 5).split(":").map(Number);
  if (Number.isNaN(hourRaw) || Number.isNaN(minuteRaw)) return null;
  return hourRaw * 60 + minuteRaw;
};

const formatDuration = (start, end) => {
  const startMin = toMinutes(start);
  const endMin = toMinutes(end);

  if (startMin === null || endMin === null) return "No disponible";

  let diff = endMin - startMin;
  if (diff < 0) diff += 24 * 60;

  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;

  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes} min`;
};

const coversHourSlot = (start, end, hour) => {
  const startMin = toMinutes(start);
  const endMinRaw = toMinutes(end);
  if (startMin === null || endMinRaw === null) return false;

  let endMin = endMinRaw;
  if (endMin <= startMin) {
    endMin += 24 * 60;
  }

  const hourStart = hour * 60;
  const hourEnd = hourStart + 60;

  return startMin < hourEnd && endMin > hourStart;
};

const getMonthKeyFromDia = (diaValue) => {
  if (!diaValue) return "";
  const dayKey = String(diaValue).slice(0, 10);
  const [year, month] = dayKey.split("-");
  if (!year || !month) return "";
  return `${year}-${month}`;
};

const getSolicitanteKey = (solicitud) => {
  const correo = String(solicitud?.correo_Electronico || "").trim().toLowerCase();
  if (correo) return `correo:${correo}`;

  const nombreUsuario = String(
    solicitud?.solicitante_nombre_usuario || solicitud?.nombre_Usuario || ""
  ).trim().toLowerCase();
  if (nombreUsuario) return `usuario:${nombreUsuario}`;

  const nombre = String(
    solicitud?.solicitante_nombre || solicitud?.nombre || ""
  ).trim().toLowerCase();
  const apellido = String(
    solicitud?.solicitante_apellido || solicitud?.apellido || ""
  ).trim().toLowerCase();
  const fullName = `${nombre} ${apellido}`.trim();
  if (fullName) return `nombre:${fullName}`;

  return "";
};

const getSolicitanteNombre = (solicitud) => {
  const nombre = String(
    solicitud?.solicitante_nombre || solicitud?.nombre || ""
  ).trim();
  const apellido = String(
    solicitud?.solicitante_apellido || solicitud?.apellido || ""
  ).trim();
  const fullName = `${nombre} ${apellido}`.trim();
  if (fullName) return fullName;

  const nombreUsuario = String(
    solicitud?.solicitante_nombre_usuario || solicitud?.nombre_Usuario || ""
  ).trim();
  if (nombreUsuario) return nombreUsuario;

  return "No disponible";
};

const getSolicitanteCorreo = (solicitud) => {
  const correo = String(solicitud?.correo_Electronico || "").trim();
  if (correo) return correo;
  return "No disponible";
};

const getNivelSala = (solicitud) => {
  const nivel = String(
    solicitud?.n_Piso ?? solicitud?.N_piso ?? solicitud?.nivel ?? solicitud?.Nivel ?? ""
  ).trim();
  if (!nivel) return "No disponible";
  return nivel;
};

const getSalaConEdificio = (solicitud) => {
  const sala = String(solicitud?.nombre_Sala || "").trim();
  const edificio = String(solicitud?.edificio || "").trim();

  if (sala && edificio) return `${sala} (${edificio})`;
  if (sala) return sala;
  if (edificio) return edificio;
  return "Sin sala";
};

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }

// ─── inline styles ────────────────────────────────────────────────────────────
const S = {
  page: {
    fontFamily: "'Segoe UI', Tahoma, sans-serif",
    background: "#eef2f7",
    minHeight: "100vh",
  },
  container: { maxWidth: 1280, margin: "0 auto", padding: "24px 16px" },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 3px 16px rgba(0,0,0,0.08)",
    padding: 24,
    marginBottom: 28,
  },
  sectionTitle: {
    fontSize: 18, fontWeight: 700, color: "#1e3a5f",
    marginBottom: 16, borderLeft: "4px solid #3b82f6", paddingLeft: 12,
  },
  filterRow: {
    display: "flex", alignItems: "center", gap: 12,
    marginBottom: 16, flexWrap: "wrap",
  },
  select: {
    padding: "8px 16px", borderRadius: 8, border: "1.5px solid #cbd5e1",
    fontSize: 14, color: "#1e3a5f", background: "#f8fafc",
    cursor: "pointer", outline: "none", minWidth: 200,
  },
  // view toggle tabs
  tabs: {
    display: "flex", gap: 4, background: "#f1f5f9",
    borderRadius: 10, padding: 4, marginBottom: 20, width: "fit-content",
  },
  tab: (active) => ({
    padding: "7px 20px", borderRadius: 8, border: "none", cursor: "pointer",
    fontSize: 13, fontWeight: 600,
    background: active ? "#3b82f6" : "transparent",
    color: active ? "#fff" : "#64748b",
    transition: "all 0.18s",
  }),
  // calendar nav bar
  calNavBar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 10, marginBottom: 16,
    background: "linear-gradient(90deg, #1e3a5f 0%, #2563eb 100%)",
    borderRadius: 12, padding: "12px 20px",
    boxShadow: "0 2px 10px rgba(37,99,235,0.18)",
  },
  calNavBtn: {
    background: "rgba(255,255,255,0.15)", color: "#fff",
    border: "1.5px solid rgba(255,255,255,0.35)", borderRadius: 8,
    padding: "6px 16px", cursor: "pointer", fontWeight: 700, fontSize: 14,
  },
  calLabel: { color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: 0.5 },
  calGrid7: { display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 5 },
  calDayHeader: {
    textAlign: "center", fontWeight: 700, fontSize: 12,
    color: "#64748b", padding: "6px 0", textTransform: "uppercase",
  },
  cell: (isToday, hasEv, hasPending, faded) => ({
    minHeight: 80, borderRadius: 10, padding: 6,
    background: isToday ? "#eff6ff" : hasPending ? "#fffbeb" : hasEv ? "#f0fdf4" : "#fafafa",
    border: isToday ? "2px solid #3b82f6" : hasPending ? "1.5px solid #fcd34d" : hasEv ? "1.5px solid #86efac" : "1.5px solid #e2e8f0",
    opacity: faded ? 0.35 : 1,
    cursor: hasEv ? "pointer" : "default",
    transition: "box-shadow 0.15s",
  }),
  dayNum: (isToday) => ({
    fontSize: 13, fontWeight: isToday ? 800 : 500,
    color: isToday ? "#2563eb" : "#374151", marginBottom: 4,
  }),
  evChip: (estado) => ({
    fontSize: 10, borderRadius: 4, padding: "1px 5px", marginBottom: 2,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    background: estado==="Pendiente"?"#fef3c7":estado==="Aceptada"?"#d1fae5":estado==="Cancelada"?"#fee2e2":"#e0e7ff",
    color: estado==="Pendiente"?"#92400e":estado==="Aceptada"?"#065f46":estado==="Cancelada"?"#991b1b":"#3730a3",
    fontWeight: 600,
  }),
  badge: (estado) => ({
    display: "inline-block", padding: "2px 10px", borderRadius: 20,
    fontSize: 11, fontWeight: 600,
    background: estado==="Pendiente"?"#fef3c7":estado==="Aceptada"?"#d1fae5":estado==="Cancelada"?"#fee2e2":"#e0e7ff",
    color: estado==="Pendiente"?"#92400e":estado==="Aceptada"?"#065f46":estado==="Cancelada"?"#991b1b":"#3730a3",
  }),
  // day-view hour rows
  hourRow: {
    display: "grid", gridTemplateColumns: "56px 1fr",
    borderBottom: `1px solid ${CAL_GRID_LINE}`, minHeight: 44,
    alignItems: "flex-start",
  },
  hourLabel: { fontSize: 11, color: "#7f93ad", padding: "4px 8px 0 0", textAlign: "right", fontWeight: 600 },
  // week view
  weekGrid: (n) => ({
    display: "grid",
    gridTemplateColumns: `56px repeat(${n},1fr)`,
    borderBottom: `1px solid ${CAL_GRID_LINE}`, minHeight: 44,
    alignItems: "flex-start",
  }),
  // popup
  popOverlay: {
    position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",
    zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",
  },
  popup: {
    background:"#fff",
    borderRadius:14,
    width:"min(92vw,560px)",
    maxHeight:"min(84vh,760px)",
    boxShadow:"0 8px 40px rgba(0,0,0,0.18)",
    display:"flex",
    flexDirection:"column",
    overflow:"hidden",
  },
  popHeader: {
    display:"flex",
    alignItems:"center",
    justifyContent:"space-between",
    gap:12,
    padding:"16px 18px 12px",
    borderBottom:"1px solid #e2e8f0",
    background:"#f8fafc",
  },
  popTitle: { fontSize:16,fontWeight:700,color:"#1e3a5f",marginBottom:2 },
  popSubtitle: { fontSize:12, color:"#64748b", fontWeight:600 },
  popList: {
    overflowY:"auto",
    padding:"4px 18px 0",
    maxHeight:"calc(min(84vh,760px) - 130px)",
  },
  popItem: {
    borderBottom:"1px solid #f1f5f9",
    padding:"12px 0",
    fontSize:13,
    color:"#374151",
    lineHeight:1.45,
    wordBreak:"break-word",
  },
  popActions: {
    marginTop: 8,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  popActionBtn: (variant) => ({
    border: "none",
    borderRadius: 8,
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 12,
    color: "#fff",
    background: variant === "approve" ? "#16a34a" : "#ef4444",
  }),
  popFooter: {
    borderTop:"1px solid #e2e8f0",
    padding:"12px 18px 14px",
    display:"flex",
    justifyContent:"flex-end",
    background:"#fff",
  },
  popCloseTop: {
    border:"none",
    background:"transparent",
    color:"#64748b",
    fontSize:20,
    fontWeight:700,
    lineHeight:1,
    cursor:"pointer",
    padding:0,
    margin:0,
  },
  popClose: {
    background:"#3b82f6",
    color:"#fff",
    border:"none",
    borderRadius:8,
    padding:"8px 22px",
    cursor:"pointer",
    fontWeight:700,
  },
};

// ─── build event map: "YYYY-MM-DD" → [sol, ...] ───────────────────────────────
function buildMap(solicitudes) {
  const map = {};
  solicitudes.forEach(s => {
    const estado = String(s.confirmacion || "").toLowerCase();
    if (estado !== "aceptada" && estado !== "pendiente") return;
    const key = s.dia ? s.dia.slice(0, 10) : null;
    if (!key) return;
    if (!map[key]) map[key] = [];
    map[key].push(s);
  });

  // Prioridad visual: Pendiente primero, luego Aceptada y por hora de inicio.
  Object.keys(map).forEach((key) => {
    map[key].sort((a, b) => {
      const aEstado = String(a.confirmacion || "").toLowerCase();
      const bEstado = String(b.confirmacion || "").toLowerCase();
      const aPend = aEstado === "pendiente" ? 0 : 1;
      const bPend = bEstado === "pendiente" ? 0 : 1;
      if (aPend !== bPend) return aPend - bPend;

      const aHora = String(a.hora_Inicio || "");
      const bHora = String(b.hora_Inicio || "");
      return aHora.localeCompare(bHora);
    });
  });

  return map;
}

// ─── HOURS for day/week view ──────────────────────────────────────────────────
const HOURS = Array.from({length: 24}, (_, i) => i); // 0..23

// ─── DAY VIEW ─────────────────────────────────────────────────────────────────
function DayView({ date, eventMap, onSelectEvents }) {
  const key = ymd(date);
  const events = eventMap[key] || [];
  const isToday = sameDay(date, new Date());

  return (
    <div>
      <div style={{
        textAlign:"center", fontWeight:700, fontSize:20, color: isToday?"#2563eb":"#1e3a5f",
        marginBottom:16,
        background: isToday?"#eff6ff":"#f8fafc",
        borderRadius:10, padding:"12px 0",
        border: isToday?"2px solid #3b82f6":"1.5px solid #e2e8f0",
      }}>
        {DAYS_FULL[date.getDay()]}, {date.getDate()} de {MONTHS[date.getMonth()]} {date.getFullYear()}
        {isToday && <span style={{marginLeft:10,fontSize:12,background:"#3b82f6",color:"#fff",borderRadius:20,padding:"2px 10px"}}>Hoy</span>}
      </div>

      {/* show all events for the day */}
      {events.length === 0 ? (
        <div style={{textAlign:"center",color:"#94a3b8",padding:"32px 0",fontSize:14}}>
          Sin solicitudes para este día
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {events.map((ev, i) => (
            <div key={i} style={{
              borderRadius:10, padding:"12px 16px",
              background: ev.confirmacion==="Pendiente"?"#fffbeb":ev.confirmacion==="Aceptada"?"#f0fdf4":"#fef2f2",
              border: `1.5px solid ${ev.confirmacion==="Pendiente"?"#fcd34d":ev.confirmacion==="Aceptada"?"#86efac":"#fca5a5"}`,
              display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,
            }}>
              <div>
                <strong style={{color:"#1e3a5f"}}>{ev.id_Solicitud}</strong>
                <span style={{margin:"0 8px",color:"#94a3b8"}}>·</span>
                <span style={{color:"#374151"}}>{ev.nombre_Sala} ({ev.edificio})</span>
                <div style={{ marginTop: 4, color: "#475569", fontSize: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <span><strong>Nivel:</strong> {getNivelSala(ev)}</span>
                  <span><strong>Solicitante:</strong> {getSolicitanteNombre(ev)}</span>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:13,color:"#64748b"}}>🕐 {ev.hora_Inicio} – {ev.hora_Fin}</span>
                <span style={S.badge(ev.confirmacion)}>{ev.confirmacion}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* hour timeline */}
      <div style={{marginTop:24}}>
        <div style={{fontSize:12,color:"#94a3b8",marginBottom:8,fontWeight:600}}>LÍNEA DE TIEMPO</div>
        {HOURS.map(h => {
          const hEvents = events.filter(ev => {
              return coversHourSlot(ev.hora_Inicio, ev.hora_Fin, h);
          });
          return (
            <div key={h} style={S.hourRow}>
              <div style={S.hourLabel}>{pad(h)}:00</div>
              <div style={{padding:"4px 0",borderLeft:`2px solid ${CAL_GRID_LINE}`,paddingLeft:8}}>
                {hEvents.map((ev,i) => (
                  <div key={i} style={{...S.evChip(ev.confirmacion),display:"inline-block",marginRight:6}}>
                    {ev.hora_Inicio}–{ev.hora_Fin} {getSalaConEdificio(ev)}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── WEEK VIEW ────────────────────────────────────────────────────────────────
function WeekView({ date, eventMap, onSelectDay }) {
  // find Monday of current week (or Sunday if you prefer)
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - date.getDay()); // Sunday
  const days = Array.from({length:7}, (_,i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });
  const today = new Date();

  return (
    <div>
      {/* header row */}
      <div style={{display:"grid",gridTemplateColumns:"56px repeat(7,1fr)",gap:5,marginBottom:8}}>
        <div/>
        {days.map((d,i) => {
          const isToday = sameDay(d, today);
          return (
            <div key={i} style={{
              textAlign:"center",fontWeight:700,fontSize:12,
              color: isToday?"#2563eb":"#64748b",
              background: isToday?"#eff6ff":"transparent",
              borderRadius:8,padding:"6px 2px",
              border: isToday?"2px solid #3b82f6":"none",
            }}>
              <div style={{textTransform:"uppercase",fontSize:11}}>{DAYS_SHORT[d.getDay()]}</div>
              <div style={{fontSize:18}}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      {/* hour rows */}
      {HOURS.map(h => (
        <div key={h} style={{display:"grid",gridTemplateColumns:"56px repeat(7,1fr)",gap:5,borderBottom:`1px solid ${CAL_GRID_LINE}`,minHeight:44}}>
          <div style={S.hourLabel}>{pad(h)}:00</div>
          {days.map((d,i) => {
            const key = ymd(d);
            const events = (eventMap[key]||[]).filter(ev => {
              return coversHourSlot(ev.hora_Inicio, ev.hora_Fin, h);
            });
            return (
              <div key={i} style={{borderLeft:`1px solid ${CAL_GRID_LINE}`,padding:"2px 3px"}}>
                {events.map((ev,j) => (
                  <div key={j}
                    style={{...S.evChip(ev.confirmacion),cursor:"pointer"}}
                    onClick={() => onSelectDay && onSelectDay(d, eventMap[key]||[])}
                  >
                    {ev.hora_Inicio}-{ev.hora_Fin} {getSalaConEdificio(ev)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── MONTH VIEW ───────────────────────────────────────────────────────────────
function MonthView({ year, month, eventMap, onSelectDay }) {
  const today = new Date();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div style={S.calGrid7}>
        {DAYS_SHORT.map(d => <div key={d} style={S.calDayHeader}>{d}</div>)}
        {cells.map((day, idx) => {
          if (day === null) return <div key={`b-${idx}`}/>;
          const key = `${year}-${pad(month+1)}-${pad(day)}`;
          const events = eventMap[key] || [];
          const hasEv = events.length > 0;
          const hasPending = events.some(ev => String(ev.confirmacion || "").toLowerCase() === "pendiente");
          const isToday = day===today.getDate() && month===today.getMonth() && year===today.getFullYear();
          return (
            <div key={key} style={S.cell(isToday,hasEv,hasPending,false)}
              onClick={() => hasEv && onSelectDay && onSelectDay(new Date(year,month,day), events)}>
              <div style={S.dayNum(isToday)}>{day}</div>
              {events.slice(0,2).map((ev,i)=>(
                <div key={i} style={S.evChip(ev.confirmacion)}>{getSalaConEdificio(ev)}</div>
              ))}
              {events.length>2 && <div style={{fontSize:10,color:"#64748b"}}>+{events.length-2} más</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── YEAR VIEW ────────────────────────────────────────────────────────────────
function YearView({ year, eventMap, onMonthClick }) {
  const today = new Date();
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16}}>
      {MONTHS.map((mName,mIdx) => {
        const daysInM = getDaysInMonth(year, mIdx);
        const firstD = getFirstDayOfMonth(year, mIdx);
        const cells = [];
        for (let i=0;i<firstD;i++) cells.push(null);
        for (let d=1;d<=daysInM;d++) cells.push(d);

        // count events this month
        let totalEv = 0;
        let pendingEv = 0;
        for (let d=1;d<=daysInM;d++) {
          const k=`${year}-${pad(mIdx+1)}-${pad(d)}`;
          const dayEvents = eventMap[k] || [];
          totalEv += dayEvents.length;
          dayEvents.forEach((ev) => {
            if ((ev.confirmacion || "").toLowerCase() === "pendiente") pendingEv += 1;
          });
        }
        const hasEvMonth = totalEv > 0;
        const hasPendingMonth = pendingEv > 0;

        return (
          <div key={mIdx}
            onClick={() => onMonthClick && onMonthClick(mIdx)}
            style={{
              borderRadius:12,border:"1.5px solid #e2e8f0",padding:12,cursor:"pointer",
              background: hasPendingMonth?"#fffbeb":hasEvMonth?"#f0fdf4":"#fafafa",
              boxShadow: hasPendingMonth?"0 2px 8px rgba(252,211,77,0.25)":hasEvMonth?"0 2px 8px rgba(74,222,128,0.15)":"none",
              transition:"box-shadow 0.15s",
            }}
          >
            <div style={{fontWeight:700,fontSize:13,color:"#1e3a5f",marginBottom:8,
              display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              {mName}
              {totalEv>0 && (
                <span style={{fontSize:11,background:hasPendingMonth?"#f59e0b":"#22c55e",color:"#fff",
                  borderRadius:20,padding:"1px 8px",fontWeight:700}}>
                  {totalEv}
                </span>
              )}
            </div>
            {/* mini month grid */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
              {DAYS_SHORT.map(d=>(
                <div key={d} style={{fontSize:8,color:"#94a3b8",textAlign:"center"}}>{d[0]}</div>
              ))}
              {cells.map((day,idx) => {
                if (!day) return <div key={`b-${idx}`}/>;
                const k=`${year}-${pad(mIdx+1)}-${pad(day)}`;
                const dayEvents = eventMap[k] || [];
                const hasEv = dayEvents.length > 0;
                const hasPendingDay = dayEvents.some(ev => (ev.confirmacion || "").toLowerCase() === "pendiente");
                const isToday=day===today.getDate()&&mIdx===today.getMonth()&&year===today.getFullYear();
                return (
                  <div key={k} style={{
                    width:"100%",aspectRatio:"1",borderRadius:3,
                    background:isToday?"#3b82f6":hasPendingDay?"#fcd34d":hasEv?"#86efac":"transparent",
                    border:isToday?"2px solid #2563eb":"none",
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:9,color:isToday?"#fff":hasPendingDay?"#78350f":hasEv?"#065f46":"#94a3b8",
                    fontWeight:hasEv||isToday?700:400,
                  }}>{day}</div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── MAIN CALENDAR COMPONENT ──────────────────────────────────────────────────
function SolicitudesCalendar({ solicitudes, isSuperAdmin, onApprove, onCancel }) {
  const today = new Date();
  const [view, setView]       = useState("mes");    // dia | semana | mes | año
  const [curDate, setCurDate] = useState(new Date());
  const [viewYear, setViewYear]   = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [popup, setPopup]     = useState(null); // {date, events}
  const [cancelandoId, setCancelandoId] = useState(null);

  const eventMap = buildMap(solicitudes);
  const solicitudesPorSolicitanteMes = useMemo(() => {
    const counter = {};

    solicitudes.forEach((solicitud) => {
      const solicitanteKey = getSolicitanteKey(solicitud);
      const monthKey = getMonthKeyFromDia(solicitud?.dia);
      if (!solicitanteKey || !monthKey) return;

      const counterKey = `${solicitanteKey}|${monthKey}`;
      counter[counterKey] = (counter[counterKey] || 0) + 1;
    });

    return counter;
  }, [solicitudes]);

  const getSolicitudesMes = (solicitud) => {
    const solicitanteKey = getSolicitanteKey(solicitud);
    const monthKey = getMonthKeyFromDia(solicitud?.dia);
    if (!solicitanteKey || !monthKey) return 0;

    return solicitudesPorSolicitanteMes[`${solicitanteKey}|${monthKey}`] || 0;
  };

  // ── navigation ─────────────────────────────────────────────────────────────
  const navigate = (dir) => {
    if (view === "dia") {
      const d = new Date(curDate);
      d.setDate(d.getDate() + dir);
      setCurDate(d);
    } else if (view === "semana") {
      const d = new Date(curDate);
      d.setDate(d.getDate() + dir * 7);
      setCurDate(d);
    } else if (view === "mes") {
      let m = viewMonth + dir;
      let y = viewYear;
      if (m < 0)  { m = 11; y--; }
      if (m > 11) { m = 0;  y++; }
      setViewMonth(m); setViewYear(y);
    } else if (view === "año") {
      setViewYear(y => y + dir);
    }
  };

  const goToday = () => {
    const t = new Date();
    setCurDate(t); setViewYear(t.getFullYear()); setViewMonth(t.getMonth());
  };

  // ── label for nav bar ──────────────────────────────────────────────────────
  const navLabel = () => {
    if (view === "dia") return `${DAYS_FULL[curDate.getDay()]}, ${curDate.getDate()} ${MONTHS_SHORT[curDate.getMonth()]} ${curDate.getFullYear()}`;
    if (view === "semana") {
      const start = new Date(curDate);
      start.setDate(curDate.getDate() - curDate.getDay());
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return `${start.getDate()} ${MONTHS_SHORT[start.getMonth()]} – ${end.getDate()} ${MONTHS_SHORT[end.getMonth()]} ${end.getFullYear()}`;
    }
    if (view === "mes") return `${MONTHS[viewMonth]} ${viewYear}`;
    return `${viewYear}`;
  };

  // ── year selectors ─────────────────────────────────────────────────────────
  const years = [];
  for (let y=2020; y<=today.getFullYear()+2; y++) years.push(y);

  return (
    <div>
      {/* ── view tabs ── */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10,marginBottom:12}}>
        <div style={S.tabs}>
          {[["dia","📅 Día"],["semana","📆 Semana"],["mes","🗓️ Mes"],["año","📊 Año"]].map(([v,label])=>(
            <button key={v} style={S.tab(view===v)} onClick={()=>setView(v)}>{label}</button>
          ))}
        </div>
        {/* Legend */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {["Pendiente","Aceptada","Cancelada"].map(e=>(
            <span key={e} style={{...S.badge(e),fontSize:11}}>● {e}</span>
          ))}
        </div>
      </div>

      {/* ── nav bar ── */}
      <div style={S.calNavBar}>
        <button style={S.calNavBtn} onClick={()=>navigate(-1)}>‹ Anterior</button>
        <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <span style={S.calLabel}>{navLabel()}</span>
          {view === "mes" && (
            <div style={{display:"flex",gap:6,background:"rgba(255,255,255,0.12)",borderRadius:8,padding:"4px 10px"}}>
              <label style={{color:"rgba(255,255,255,0.85)",fontSize:12,fontWeight:600,margin:0}}>Año</label>
              <select value={viewYear} onChange={e=>{setViewYear(Number(e.target.value))}}
                style={{background:"#fff",border:"none",borderRadius:6,padding:"3px 6px",fontSize:13,fontWeight:600,color:"#1e3a5f",cursor:"pointer",outline:"none"}}>
                {years.map(y=><option key={y} value={y}>{y}</option>)}
              </select>
              <label style={{color:"rgba(255,255,255,0.85)",fontSize:12,fontWeight:600,margin:0}}>Mes</label>
              <select value={viewMonth} onChange={e=>{setViewMonth(Number(e.target.value))}}
                style={{background:"#fff",border:"none",borderRadius:6,padding:"3px 6px",fontSize:13,fontWeight:600,color:"#1e3a5f",cursor:"pointer",outline:"none"}}>
                {MONTHS.map((m,i)=><option key={m} value={i}>{m}</option>)}
              </select>
            </div>
          )}
          {view === "año" && (
            <div style={{display:"flex",gap:6,background:"rgba(255,255,255,0.12)",borderRadius:8,padding:"4px 10px"}}>
              <label style={{color:"rgba(255,255,255,0.85)",fontSize:12,fontWeight:600,margin:0}}>Año</label>
              <select value={viewYear} onChange={e=>{setViewYear(Number(e.target.value))}}
                style={{background:"#fff",border:"none",borderRadius:6,padding:"3px 6px",fontSize:13,fontWeight:600,color:"#1e3a5f",cursor:"pointer",outline:"none"}}>
                {years.map(y=><option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          <button onClick={goToday} style={{
            background:"rgba(255,255,255,0.9)",color:"#1e3a5f",border:"none",
            borderRadius:8,padding:"5px 14px",cursor:"pointer",fontWeight:700,fontSize:12,
          }}>Hoy</button>
        </div>
        <button style={S.calNavBtn} onClick={()=>navigate(1)}>Siguiente ›</button>
      </div>

      {/* ── view body ── */}
      <div style={{overflow:"auto"}}>
        {view === "dia" && (
          <DayView date={curDate} eventMap={eventMap} />
        )}
        {view === "semana" && (
          <WeekView date={curDate} eventMap={eventMap}
            onSelectDay={(d,evs)=>setPopup({date:d,events:evs})} />
        )}
        {view === "mes" && (
          <MonthView year={viewYear} month={viewMonth} eventMap={eventMap}
            onSelectDay={(d,evs)=>setPopup({date:d,events:evs})} />
        )}
        {view === "año" && (
          <YearView year={viewYear} eventMap={eventMap}
            onMonthClick={(mIdx)=>{ setViewMonth(mIdx); setView("mes"); }} />
        )}
      </div>

      {/* ── day detail popup ── */}
      {popup && (
        <div style={S.popOverlay} onClick={()=>setPopup(null)}>
          <div style={S.popup} onClick={e=>e.stopPropagation()}>
            <div style={S.popHeader}>
              <div>
                <div style={S.popTitle}>
                  Solicitudes - {popup.date.getDate()} de {MONTHS[popup.date.getMonth()]} {popup.date.getFullYear()}
                </div>
                <div style={S.popSubtitle}>{popup.events.length} resultado(s)</div>
              </div>
              <button style={S.popCloseTop} onClick={()=>setPopup(null)} aria-label="Cerrar">x</button>
            </div>
            <div style={S.popList}>
              {popup.events.map((ev,i)=>(
                <div key={`${ev.id_Solicitud || "item"}-${i}`} style={{...S.popItem, borderBottom: i === popup.events.length - 1 ? "none" : S.popItem.borderBottom}}>
                  <strong>{ev.id_Solicitud}</strong> · {ev.nombre_Sala} ({ev.edificio})<br/>
                  🕐 {ev.hora_Inicio} - {ev.hora_Fin} &nbsp;
                  <span style={S.badge(ev.confirmacion)}>{ev.confirmacion}</span>
                  <div style={{ marginTop: 6, color: "#475569", fontSize: 12 }}>
                    <div><strong>Duración:</strong> {formatDuration(ev.hora_Inicio, ev.hora_Fin)}</div>
                    <div><strong>Solicitante:</strong> {getSolicitanteNombre(ev)}</div>
                    <div><strong>Correo:</strong> {getSolicitanteCorreo(ev)}</div>
                    <div><strong>Solicitudes este mes:</strong> {getSolicitudesMes(ev)}</div>
                  </div>
                  <div style={S.popActions}>
                    {isSuperAdmin && ev.confirmacion === "Pendiente" && onApprove && (
                      <button
                        style={S.popActionBtn("approve")}
                        onClick={async () => {
                          await onApprove(ev);
                          setPopup(null);
                        }}
                      >
                        Aprobar
                      </button>
                    )}
                    {String(ev.confirmacion || "").trim().toLowerCase() === "pendiente" && onCancel && (
                      <button
                        style={{ ...S.popActionBtn("cancel"), opacity: cancelandoId === ev.id_Solicitud ? 0.7 : 1 }}
                        disabled={cancelandoId === ev.id_Solicitud}
                        onClick={async () => {
                          setCancelandoId(ev.id_Solicitud);
                          try {
                            await onCancel(ev);
                            setPopup(null);
                          } finally {
                            setCancelandoId(null);
                          }
                        }}
                      >
                        {cancelandoId === ev.id_Solicitud ? "Cancelando..." : "Cancelar"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div style={S.popFooter}>
              <button style={S.popClose} onClick={()=>setPopup(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
function Ver() {
  const cookies = new Cookies();

  const parseRoleName = (value) => {
    if (value === undefined || value === null) return "";
    const raw = String(value);
    try {
      return decodeURIComponent(raw);
    } catch (e) {
      return raw;
    }
  };

  const correo_Electronico = cookies.get("correo_Electronico");
  const rol = parseRoleName(cookies.get("nombre_Rol"));
  const rolNormalizado = rol.trim().toLowerCase();
  const isSuperAdmin = rolNormalizado === "super admin";
  const canCreateReserva = rol.trim().toLowerCase() !== "usuario";

  const [lgShow,  setLgShow]  = useState(false);
  const [lgShow2, setLgShow2] = useState(false);

  const [allSolicitudes, setAllSolicitudes] = useState([]);
  const [filtro,         setFiltro]         = useState("todas");
  const [loadingAll,     setLoadingAll]     = useState(false);

  const [showDisponibilidad, setShowDisponibilidad] = useState(false);
  const [listaEdificios, setListaEdificios] = useState([]);
  const [salasDisponibilidad, setSalasDisponibilidad] = useState([]);
  const [edificioDisp, setEdificioDisp] = useState("");
  const [salaDisp, setSalaDisp] = useState("");
  const [diaDisp, setDiaDisp] = useState(ymd(new Date()));
  const [loadingDisp, setLoadingDisp] = useState(false);
  const [consultadoDisp, setConsultadoDisp] = useState(false);
  const [resultadoDisp, setResultadoDisp] = useState([]);

  const [showCrearReserva, setShowCrearReserva] = useState(false);
  const [salasCrear, setSalasCrear] = useState([]);
  const [usuariosCrear, setUsuariosCrear] = useState([]);
  const [edificioCrear, setEdificioCrear] = useState("");
  const [salaCrear, setSalaCrear] = useState("");
  const [usarRangoCrear, setUsarRangoCrear] = useState(false);
  const [diaCrear, setDiaCrear] = useState(ymd(new Date()));
  const [diaInicioCrear, setDiaInicioCrear] = useState(ymd(new Date()));
  const [diaFinCrear, setDiaFinCrear] = useState(ymd(new Date()));
  const [inicioCrear, setInicioCrear] = useState("");
  const [finCrear, setFinCrear] = useState("");
  const [usuarioCrear, setUsuarioCrear] = useState("");
  const [mensajeCrear, setMensajeCrear] = useState("");
  const [motivoCrear, setMotivoCrear] = useState("");
  const [loadingCrear, setLoadingCrear] = useState(false);
  const [progresoCrear, setProgresoCrear] = useState({ actual: 0, total: 0 });

  const [solicitudes5, setSolicitudes5] = useState([]);

  const [codigoS,    setCodigoS]    = useState("");
  const [nombre1,    setNombre1]    = useState("");
  const [identidad1, setIdentidad1] = useState("");
  const [nombre2,    setNombre2]    = useState("");
  const [identidad2, setIdentidad2] = useState("");
  const [nombre3,    setNombre3]    = useState("");
  const [identidad3, setIdentidad3] = useState("");
  const [nombre4,    setNombre4]    = useState("");
  const [identidad4, setIdentidad4] = useState("");

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const fetchAll = async () => {
    setLoadingAll(true);
    try {
      let data = [];

      if (isSuperAdmin) {
        const res = await fetch(`${api}/mostrar`);
        const globalRows = await res.json();
        data = globalRows.filter(r => r.confirmacion === "Pendiente" || r.confirmacion === "Aceptada");
      } else {
        const res = await fetch(`${api}/consultarReservas`, {
          method:"PUT", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ correo_Electronico }),
        });
        data = await res.json();
      }

      data.forEach(r => {
        const codigo = String(r.id_Solicitud || "");
        r.id_Solicitud = codigo.startsWith("RS") ? codigo : `RS${codigo}`;
        r.dia = r.dia ? r.dia.slice(0,10) : "";
      });
      setAllSolicitudes(data);
    } catch(e) { console.error(e); }
    setLoadingAll(false);
  };

  const hoyVentana = new Date();
  const inicioVentana = ymd(hoyVentana);
  const finVentana    = ymd(new Date(hoyVentana.getFullYear(), hoyVentana.getMonth() + 3, 0));

  const cumpleFiltroEstado = (s) => {
    if (filtro === "pendientes") return s.confirmacion === "Pendiente";
    if (filtro === "aceptadas")  return s.confirmacion === "Aceptada";
    if (filtro === "canceladas") return s.confirmacion === "Cancelada";
    if (filtro === "datacenter") return s.edificio && s.edificio.toLowerCase().includes("data");
    return true; // "todas"
  };

  const ordenarPorPrioridad = (a, b) => {
    const aPend = a.confirmacion === "Pendiente" ? 0 : 1;
    const bPend = b.confirmacion === "Pendiente" ? 0 : 1;
    if (aPend !== bPend) return aPend - bPend;

    const diaCmp = String(a.dia || "").localeCompare(String(b.dia || ""));
    if (diaCmp !== 0) return diaCmp;
    return String(a.hora_Inicio || "").localeCompare(String(b.hora_Inicio || ""));
  };

  // Calendario: respeta el filtro de estado, pero muestra todas las fechas (pasadas y futuras)
  const calendarSolicitudes = allSolicitudes.filter(cumpleFiltroEstado).sort(ordenarPorPrioridad);

  // Tabla: además acotada a la ventana actual (hoy -> +3 meses)
  const filteredSolicitudes = allSolicitudes
    .filter(s => {
      const dia = String(s.dia || "");
      if (dia < inicioVentana || dia > finVentana) return false;
      return cumpleFiltroEstado(s);
    })
    .sort(ordenarPorPrioridad);

  const cargarEdificiosDisponibilidad = async () => {
    try {
      const res = await fetch(`${api}/edificios`);
      const data = await res.json();
      setListaEdificios(data);
    } catch (e) {
      console.error(e);
    }
  };

  const abrirDisponibilidad = async () => {
    setShowDisponibilidad(true);
    setConsultadoDisp(false);
    setResultadoDisp([]);
    if (listaEdificios.length === 0) {
      await cargarEdificiosDisponibilidad();
    }
  };

  const onChangeEdificioDisponibilidad = async (value) => {
    setEdificioDisp(value);
    setSalaDisp("");
    setConsultadoDisp(false);
    setResultadoDisp([]);
    if (!value) {
      setSalasDisponibilidad([]);
      return;
    }

    try {
      const res = await fetch(`${api}/sala`, {
        method: "put",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nombre: value }),
      });
      const data = await res.json();
      setSalasDisponibilidad(data);
    } catch (e) {
      console.error(e);
      setSalasDisponibilidad([]);
    }
  };

  const consultarDisponibilidad = async () => {
    if (!edificioDisp || !salaDisp || !diaDisp) {
      swal({
        title: "Validación de Datos",
        text: "Debe seleccionar edificio, sala y día",
        icon: "warning",
        button: "Aceptar",
      });
      return;
    }

    setLoadingDisp(true);
    try {
      const res = await fetch(`${api}/disponibilidad`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          edificio: edificioDisp,
          sala: salaDisp,
          dia: diaDisp,
        }),
      });
      const data = await res.json();
      setResultadoDisp(data);
      setConsultadoDisp(true);
    } catch (e) {
      console.error(e);
      swal({
        title: "Error",
        text: "No se pudo consultar la disponibilidad",
        icon: "error",
        button: "Aceptar",
      });
    }
    setLoadingDisp(false);
  };

  const cargarUsuariosCrear = async () => {
    try {
      const res = await fetch(`${api}/cUsuario`);
      const data = await res.json();
      setUsuariosCrear(data);
    } catch (e) {
      console.error(e);
    }
  };

  const abrirCrearReserva = async () => {
    setShowCrearReserva(true);
    if (listaEdificios.length === 0) {
      await cargarEdificiosDisponibilidad();
    }
    if (usuariosCrear.length === 0) {
      await cargarUsuariosCrear();
    }
  };

  const limpiarCrearReserva = () => {
    setEdificioCrear("");
    setSalaCrear("");
    setSalasCrear([]);
    setUsarRangoCrear(false);
    setDiaCrear(ymd(new Date()));
    setDiaInicioCrear(ymd(new Date()));
    setDiaFinCrear(ymd(new Date()));
    setInicioCrear("");
    setFinCrear("");
    setUsuarioCrear("");
    setMensajeCrear("");
    setMotivoCrear("");
    setProgresoCrear({ actual: 0, total: 0 });
  };

  const onChangeEdificioCrear = async (value) => {
    setEdificioCrear(value);
    setSalaCrear("");
    setSalasCrear([]);

    if (!value) return;
    try {
      const res = await fetch(`${api}/sala`, {
        method: "put",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nombre: value }),
      });
      const data = await res.json();
      setSalasCrear(data);
    } catch (e) {
      console.error(e);
    }
  };

  const generarFechasRango = (desde, hasta) => {
    const fechas = [];
    const actual = new Date(`${desde}T00:00:00`);
    const fin = new Date(`${hasta}T00:00:00`);
    while (actual <= fin) {
      fechas.push(ymd(actual));
      actual.setDate(actual.getDate() + 1);
    }
    return fechas;
  };

  const crearReservaDesdeModal = async () => {
    if (usarRangoCrear && !isSuperAdmin) {
      swal({
        title: "Acceso denegado",
        text: "Solo el Super Admin puede crear visitas por rango de fechas",
        icon: "warning",
        button: "Aceptar",
      });
      setUsarRangoCrear(false);
      return;
    }

    const fechaValida = usarRangoCrear ? (diaInicioCrear && diaFinCrear) : !!diaCrear;
    if (!edificioCrear || !salaCrear || !fechaValida || !inicioCrear || !finCrear || !usuarioCrear) {
      swal({
        title: "Validación de Datos",
        text: "Todos los campos obligatorios deben estar llenos",
        icon: "warning",
        button: "Aceptar",
      });
      return;
    }

    if (inicioCrear >= finCrear) {
      swal({
        title: "Validación de Datos",
        text: "La hora de inicio debe ser menor que la hora de finalización",
        icon: "warning",
        button: "Aceptar",
      });
      return;
    }

    if (usarRangoCrear && diaFinCrear < diaInicioCrear) {
      swal({
        title: "Validación de Datos",
        text: "La fecha final no puede ser menor que la fecha inicial",
        icon: "warning",
        button: "Aceptar",
      });
      return;
    }

    if (edificioCrear === "Data Center" && !motivoCrear.trim()) {
      swal({
        title: "Validación de Datos",
        text: "Debe ingresar el motivo de visita para Data Center",
        icon: "warning",
        button: "Aceptar",
      });
      return;
    }

    const fechas = usarRangoCrear
      ? generarFechasRango(diaInicioCrear, diaFinCrear)
      : [diaCrear];

    if (fechas.length > 120) {
      swal({
        title: "Rango demasiado grande",
        text: "Seleccione un rango de 120 días o menos",
        icon: "warning",
        button: "Aceptar",
      });
      return;
    }

    setLoadingCrear(true);
    setProgresoCrear({ actual: 0, total: usarRangoCrear ? 0 : fechas.length });
    try {
      let creadas = 0;
      let conflictosCount = 0;
      let errores = 0;

      if (usarRangoCrear) {
        const resRango = await fetch(`${api}/agregarRango`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            rol,
            Edificio: edificioCrear,
            nombre_S: salaCrear,
            fechas,
            inicio: inicioCrear,
            fin: finCrear,
            usuario: usuarioCrear,
            mensaje: mensajeCrear,
            motivo: motivoCrear,
            estado: "Aceptada",
          }),
        });

        const dataRango = await resRango.json();
        if (!resRango.ok || !dataRango?.ok) {
          throw new Error(dataRango?.message || "No se pudo crear la reserva por rango");
        }

        creadas = Number(dataRango.creadas) || 0;
        conflictosCount = Number(dataRango.conflictos) || 0;
        errores = Number(dataRango.errores) || 0;
      } else {
        const fecha = fechas[0];
        setProgresoCrear({ actual: 1, total: 1 });

        const resConflicto = await fetch(`${api}/consultarSala`, {
          method: "put",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            rol,
            edificio: edificioCrear,
            nombre: salaCrear,
            dia: fecha,
            inicio: inicioCrear,
            fin: finCrear,
          }),
        });

        const dataConflicto = await resConflicto.json();
        const conflictos = Array.isArray(dataConflicto)
          ? (Array.isArray(dataConflicto[0]) ? dataConflicto[0] : dataConflicto)
          : [];

        if (conflictos.length > 0) {
          conflictosCount = 1;
        } else {
          const resCrear = await fetch(`${api}/agregar2`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              Edificio: edificioCrear,
              nombre_S: salaCrear,
              dia: fecha,
              inicio: inicioCrear,
              fin: finCrear,
              usuario: usuarioCrear,
              mensaje: mensajeCrear,
              motivo: motivoCrear,
              estado: "Aceptada",
            }),
          });

          if (!resCrear.ok) {
            errores = 1;
          } else {
            creadas = 1;
          }
        }
      }

      if (creadas === 0 && conflictosCount > 0 && errores === 0) {
        swal({
          title: "No se pudieron crear reservas",
          text: "Todas las fechas tienen conflicto con reservas existentes",
          icon: "error",
          button: "Aceptar",
        });
      } else if (errores > 0) {
        swal({
          title: "Proceso completado con observaciones",
          text: `Creadas: ${creadas} | En conflicto: ${conflictosCount} | Con error: ${errores}`,
          icon: "warning",
          button: "Aceptar",
        });
      } else {
        swal({
          title: "Reserva(s) creada(s)",
          text: `Creadas: ${creadas}${conflictosCount > 0 ? ` | En conflicto: ${conflictosCount}` : ""}`,
          icon: "success",
          button: "Aceptar",
        });
      }

      if (creadas > 0) {
        setShowCrearReserva(false);
        limpiarCrearReserva();
        fetchAll();
      }
    } catch (e) {
      console.error(e);
      swal({
        title: "Error",
        text: e?.message || "No se pudo crear la reserva",
        icon: "error",
        button: "Aceptar",
      });
    } finally {
      setLoadingCrear(false);
      setProgresoCrear({ actual: 0, total: 0 });
    }
  };

  // ── icons ──────────────────────────────────────────────────────────────────
  const tableIcons = {
    Search:       forwardRef((p,r)=><Search {...p} ref={r}/>),
    Add:          forwardRef((p,r)=><AddBox {...p} ref={r}/>),
    Check:        forwardRef((p,r)=><Check {...p} ref={r}/>),
    Clear:        forwardRef((p,r)=><Clear {...p} ref={r}/>),
    Delete:       forwardRef((p,r)=><DeleteOutline {...p} ref={r}/>),
    DetailPanel:  forwardRef((p,r)=><ChevronRight {...p} ref={r}/>),
    Edit:         forwardRef((p,r)=><Edit {...p} ref={r}/>),
    Export:       forwardRef((p,r)=><SaveAlt {...p} ref={r}/>),
    Filter:       forwardRef((p,r)=><FilterList {...p} ref={r}/>),
    FirstPage:    forwardRef((p,r)=><FirstPage {...p} ref={r}/>),
    LastPage:     forwardRef((p,r)=><LastPage {...p} ref={r}/>),
    NextPage:     forwardRef((p,r)=><ChevronRight {...p} ref={r}/>),
    PreviousPage: forwardRef((p,r)=><ChevronLeft {...p} ref={r}/>),
    Print:        forwardRef((p,r)=><Print {...p} ref={r}/>),
    ResetSearch:  forwardRef((p,r)=><Clear {...p} ref={r}/>),
    SortArrow:    forwardRef((p,r)=><ArrowUpward {...p} ref={r}/>),
    ThirdStateCheck: forwardRef((p,r)=><Remove {...p} ref={r}/>),
    ViewColumn:   forwardRef((p,r)=><ViewColumn {...p} ref={r}/>),
    RemoveRedEyeIcon: forwardRef((p,r)=><RemoveRedEye {...p} ref={r}/>),
    Message:      forwardRef((p,r)=><Message {...p} ref={r}/>),
  };

  const columns = [
    { title:"Código",   field:"id_Solicitud" },
    { title:"Edificio", field:"edificio" },
    { title:"Sala",     field:"nombre_Sala" },
    { title:"Piso",     field:"n_Piso" },
    { title:"Día",      field:"dia" },
    { title:"Inicio",   field:"hora_Inicio" },
    { title:"Fin",      field:"hora_Fin" },
    { title:"Estado",   field:"confirmacion",
      render: row => <span style={S.badge(row.confirmacion)}>{row.confirmacion}</span> },
  ];

  const columns2 = [
    { title:"Código de Solicitud", field:"id_Solicitud" },
    { title:"Nombre de visita",    field:"Nombre" },
    { title:"Número de identidad", field:"id" },
  ];

  const enviarDatos = async () => {
    const res  = await fetch(`${api}/agregarVisitas`, {
      method:"Post", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({codigoS,nombre1,identidad1,nombre2,identidad2,nombre3,identidad3,nombre4,identidad4}),
    });
    const data = await res.json();
    if (data === "Correcto") {
      setLgShow(false);
      swal({title:"Visitas agregadas correctamente.",icon:"info",button:"Aceptar"});
    }
  };

  const cancelarSolicitud = async (rowdata) => {
    swal({
      title: "Cancelando solicitud...",
      text: "Espere un momento",
      buttons: false,
      closeOnClickOutside: false,
      closeOnEsc: false,
    });

    try {
      const res = await fetch(`${api}/cancelar`, {
        method:"delete", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          correo:   rowdata.correo_Electronico,
          cancelar: "Cancelada",
          id:       rowdata.id_Solicitud,
          edificio: rowdata.edificio,
          nombre:   rowdata.nombre_Sala,
          dia:      rowdata.dia,
          inicio:   rowdata.hora_Inicio,
          fin:      rowdata.hora_Fin,
        }),
      });

      if (!res.ok) {
        throw new Error("No se pudo cancelar la solicitud");
      }

      swal.close();
      swal({
        title:"Estado de Solicitud de Reserva",
        text:"Se realizó el cambio de estado de la solicitud de reserva",
        icon:"success",
        button:"Aceptar"
      });
      fetchAll();
    } catch (e) {
      console.error(e);
      swal.close();
      swal({
        title:"Error",
        text:"No se pudo cancelar la solicitud. Intente de nuevo.",
        icon:"error",
        button:"Aceptar"
      });
    }
  };

  const pedirComentario = async (texto) => {
    const comentario = await swal({
      text: texto,
      content: "input",
      button: {
        text: "Agregar",
        closeModal: false,
      },
    });

    if (!comentario || !String(comentario).trim()) {
      swal({
        title: "Estado de Solicitud de Reserva",
        text: "No se realizó el cambio porque no se agregó comentario",
        icon: "error",
        button: "Aceptar",
      });
      return null;
    }

    return String(comentario).trim();
  };

  const aprobarSolicitud = async (rowdata) => {
    const id = rowdata.id_Solicitud;
    const edificio = rowdata.edificio;
    const nombre = rowdata.nombre_Sala;
    const dia = rowdata.dia;
    const inicio = rowdata.hora_Inicio;
    const fin = rowdata.hora_Fin;
    const correoFila = rowdata.correo_Electronico;

    const resDisponibilidad = await fetch(`${api}/consultarSala`, {
      method: "put",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rol, edificio, nombre, dia, inicio, fin }),
    });
    const dataDisponibilidad = await resDisponibilidad.json();
    const mostrar = dataDisponibilidad[0] || [];
    const permitirAceptar =
      mostrar.length === 0 ||
      (edificio === "Torre 2" && nombre === "Zona de Descarga" && mostrar.length < 3);

    if (!permitirAceptar) {
      swal({
        title: "Solicitud en Conflicto",
        text: "No se puede aceptar porque ya existe una reserva que crea conflicto",
        icon: "error",
        button: "Aceptar",
      });
      return;
    }

    const comentario = await pedirComentario("Ingrese Comentario de Aceptación de Solicitud");
    if (!comentario) return;

    const res = await fetch(`${api}/confirmar`, {
      method: "delete",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        confirmar: "Aceptada",
        id,
        edificio,
        nombre,
        dia,
        inicio,
        fin,
        comentario,
        correo_Electronico: correoFila,
      }),
    });
    const data = await res.json();

    if (data[0] === "hecho") {
      swal({
        title: "Estado de Solicitud de Reserva",
        text: "Se aceptó correctamente la solicitud",
        icon: "success",
        button: "Aceptar",
      }).then(fetchAll);
    }
  };

  const rechazarSolicitud = async (rowdata) => {
    const comentario = await pedirComentario("Ingrese Comentario de Rechazo de Solicitud");
    if (!comentario) return;

    const res = await fetch(`${api}/rechazar`, {
      method: "delete",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        rechazada: "Rechazada",
        id: rowdata.id_Solicitud,
        edificio: rowdata.edificio,
        nombre: rowdata.nombre_Sala,
        dia: rowdata.dia,
        inicio: rowdata.hora_Inicio,
        fin: rowdata.hora_Fin,
        comentario,
        correo_Electronico: rowdata.correo_Electronico,
      }),
    });
    const data = await res.json();

    if (data[0] === "hecho") {
      swal({
        title: "Estado de Solicitud de Reserva",
        text: "Se rechazó correctamente la solicitud",
        icon: "success",
        button: "Aceptar",
      }).then(fetchAll);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <Navbar2/>
      <div style={S.container}>

        {/* ── 1. CALENDARIO (arriba) ──────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.sectionTitle}>📅 Calendario de Solicitudes</div>
          <p style={{fontSize:13,color:"#64748b",marginBottom:16}}>
            Navega por día, semana, mes o año. Haz clic en un día para ver el detalle de {isSuperAdmin ? "todas las solicitudes" : "sus solicitudes"}.
          </p>
          <SolicitudesCalendar
            solicitudes={calendarSolicitudes}
            isSuperAdmin={isSuperAdmin}
            onApprove={aprobarSolicitud}
            onCancel={cancelarSolicitud}
          />
        </div>

        {/* ── 2. TABLA (abajo) ────────────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.sectionTitle}>{isSuperAdmin ? "📋 Solicitudes Globales" : "📋 Mis Solicitudes"}</div>

          <div style={S.filterRow}>
            <label style={{fontWeight:600,color:"#1e3a5f",fontSize:14}}>Filtrar por:</label>
            <select style={S.select} value={filtro} onChange={e=>setFiltro(e.target.value)}>
              <option value="todas">Todas (Actuales)</option>
              <option value="pendientes">Solicitudes Pendientes</option>
              <option value="aceptadas">Solicitudes Aceptadas</option>
              <option value="canceladas">Solicitudes Canceladas</option>
              <option value="datacenter">Data Center</option>
            </select>
            <button
              style={{background:"#3b82f6",color:"#fff",border:"none",borderRadius:8,
                padding:"7px 18px",cursor:"pointer",fontWeight:700,fontSize:13}}
              onClick={fetchAll}
            >🔄 Actualizar</button>
            <button
              style={{
                background: "#0ea5e9",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "7px 18px",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 13,
              }}
              onClick={abrirDisponibilidad}
            >
              🔎 Consultar Disponibilidad
            </button>
            {canCreateReserva && (
              <button
                style={{
                  background: "#16a34a",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "7px 18px",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 13,
                }}
                onClick={abrirCrearReserva}
              >
                ➕ Crear reserva
              </button>
            )}
          </div>

          {loadingAll ? (
            <div style={{textAlign:"center",padding:30,color:"#64748b"}}>Cargando…</div>
          ) : (
            <div className="table-responsive">
              <MaterialTable
                options={{actionsColumnIndex:-1,pageSize:5,pageSizeOptions:[5,10,20]}}
                icons={tableIcons}
                data={filteredSolicitudes}
                columns={columns}
                title=""
                localization={{
                  toolbar:{searchPlaceholder:"Buscar…",nRowsSelected:"{0} fila(s) seleccionada(s)"},
                  header:{actions:"Acción"},
                  body:{emptyDataSourceMessage:"No se encontraron registros"},
                  pagination:{
                    labelDisplayedRows:"{from}-{to} de {count}",labelRowsSelect:"filas",
                    firstTooltip:"Primera página",previousTooltip:"Anterior",
                    nextTooltip:"Siguiente",lastTooltip:"Última página",
                  },
                }}
                actions={[
                  {
                    icon: Message, tooltip:"Ver Comentario",
                    onClick: async (event,rowdata) => {
                      const res  = await fetch(`${api}/verComentario`,{
                        method:"put",headers:{"Content-Type":"application/json"},
                        body:JSON.stringify({codigoSC:rowdata.id_Solicitud}),
                      });
                      const data2 = await res.json();
                      swal({title:"Comentario",
                        text:data2.length>0?data2[0].comentario:"No se encontró comentario",
                        icon:"info",button:"Aceptar"});
                    },
                  },
                  rowData=>({
                    icon: Check, tooltip:"Aprobar Solicitud",
                    hidden: !isSuperAdmin || rowData.confirmacion !== "Pendiente",
                    onClick: async (_,rd)=>aprobarSolicitud(rd),
                  }),
                  rowData=>({
                    icon: Clear, tooltip:"Rechazar Solicitud",
                    hidden: !isSuperAdmin || rowData.confirmacion !== "Pendiente",
                    onClick: async (_,rd)=>rechazarSolicitud(rd),
                  }),
                  rowData=>({
                    icon:DeleteOutline,tooltip:"Cancelar Solicitud",
                    hidden:rowData.confirmacion==="Cancelada",
                    onClick:(_,rd)=>cancelarSolicitud(rd),
                  }),
                  rowData=>({
                    icon:RemoveRedEye,tooltip:"Ver Lista de Visitantes",
                    hidden:!rowData.edificio||!rowData.edificio.toLowerCase().includes("data"),
                    onClick:async(_,rd)=>{
                      setLgShow2(true);
                      const res = await fetch(`${api}/consultarVisitas`,{
                        method:"PUT",headers:{"Content-Type":"application/json"},
                        body:JSON.stringify({id_Solicitudesd:rd.id_Solicitud}),
                      });
                      const data=await res.json();
                      data.forEach(r=>{r.id_Solicitud="RS"+r.id_Solicitud;});
                      setSolicitudes5(data);
                    },
                  }),
                  rowData=>({
                    icon:AddBox,tooltip:"Agregar Visitantes",
                    hidden:!rowData.edificio||!rowData.edificio.toLowerCase().includes("data"),
                    onClick:async(_,rd)=>{
                      const res=await fetch(`${api}/visitasDC`,{
                        method:"put",headers:{"Content-Type":"application/json"},
                        body:JSON.stringify({codigo_S:rd.id_Solicitud}),
                      });
                      const data=await res.json();
                      if(data[0]==="lleno"){
                        swal({title:"Ya existen Registros de Visitantes Cargados",
                          text:"Solo se puede ingresar una vez la información.",
                          icon:"warning",button:"Aceptar"});
                      } else {
                        setLgShow(true); setCodigoS(rd.id_Solicitud);
                      }
                    },
                  }),
                ]}
              />
            </div>
          )}
        </div>

      </div>

      {/* ── MODAL CREAR RESERVA ────────────────────────────────────────── */}
      <Modal size="lg" show={showCrearReserva} onHide={() => setShowCrearReserva(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Crear Reserva</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container text-start">
            <div className="row">
              <div className="p-2 col-md-6">
                <label className="form-label">Edificio</label>
                <select className="form-select" value={edificioCrear} onChange={(e) => onChangeEdificioCrear(e.target.value)}>
                  <option value="">Seleccione una opción</option>
                  {listaEdificios.map((e) => (
                    <option key={e.edificio} value={e.edificio}>{e.edificio}</option>
                  ))}
                </select>
              </div>
              <div className="p-2 col-md-6">
                <label className="form-label">Sala</label>
                <select className="form-select" value={salaCrear} onChange={(e) => setSalaCrear(e.target.value)} disabled={!edificioCrear}>
                  <option value="">Seleccione una opción</option>
                  {salasCrear.map((s) => (
                    <option key={s.nombre_Sala} value={s.nombre_Sala}>{s.nombre_Sala}</option>
                  ))}
                </select>
              </div>
            </div>

            {isSuperAdmin && (
              <div className="row pt-2">
                <div className="p-2 col-md-12">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="switch-rango-reserva"
                      checked={usarRangoCrear}
                      onChange={(e) => {
                        const activo = e.target.checked;
                        setUsarRangoCrear(activo);
                        if (activo) {
                          const base = diaCrear || ymd(new Date());
                          setDiaInicioCrear(base);
                          setDiaFinCrear(base);
                        } else {
                          setDiaCrear(diaInicioCrear || ymd(new Date()));
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor="switch-rango-reserva">
                      Crear por rango de fechas
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              {!usarRangoCrear ? (
                <div className="p-2 col-md-4">
                  <label className="form-label">Día</label>
                  <input type="date" className="form-control" value={diaCrear} onChange={(e) => setDiaCrear(e.target.value)} />
                </div>
              ) : (
                <>
                  <div className="p-2 col-md-3">
                    <label className="form-label">Desde</label>
                    <input type="date" className="form-control" value={diaInicioCrear} onChange={(e) => setDiaInicioCrear(e.target.value)} />
                  </div>
                  <div className="p-2 col-md-3">
                    <label className="form-label">Hasta</label>
                    <input type="date" className="form-control" value={diaFinCrear} onChange={(e) => setDiaFinCrear(e.target.value)} />
                  </div>
                </>
              )}

              <div className={`p-2 ${usarRangoCrear ? 'col-md-3' : 'col-md-4'}`}>
                <label className="form-label">Hora inicio</label>
                <input type="time" className="form-control" value={inicioCrear} onChange={(e) => setInicioCrear(e.target.value)} />
              </div>
              <div className={`p-2 ${usarRangoCrear ? 'col-md-3' : 'col-md-4'}`}>
                <label className="form-label">Hora fin</label>
                <input type="time" className="form-control" value={finCrear} onChange={(e) => setFinCrear(e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="p-2 col-md-12">
                <label className="form-label">Usuario</label>
                <select className="form-select" value={usuarioCrear} onChange={(e) => setUsuarioCrear(e.target.value)}>
                  <option value="">Seleccione una opción</option>
                  {usuariosCrear.map((u) => (
                    <option key={u.correo_Electronico} value={u.correo_Electronico}>{u.correo_Electronico}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row">
              <div className="p-2 col-md-12">
                <label className="form-label">Mensaje de Reserva</label>
                <input type="text" className="form-control" value={mensajeCrear} onChange={(e) => setMensajeCrear(e.target.value)} />
              </div>
            </div>

            {edificioCrear === "Data Center" && (
              <div className="row">
                <div className="p-2 col-md-12">
                  <label className="form-label">Motivo de visita</label>
                  <input type="text" className="form-control" value={motivoCrear} onChange={(e) => setMotivoCrear(e.target.value)} />
                </div>
              </div>
            )}

            <div className="row pt-3">
              <div className="col-md-12 d-flex gap-2 align-items-center flex-wrap">
                <Button variant="success" onClick={crearReservaDesdeModal} disabled={loadingCrear}>
                  {loadingCrear ? `Guardando... (${progresoCrear.actual}/${progresoCrear.total || 1})` : "Crear Reserva"}
                </Button>
                <Button
                  variant="secondary"
                  disabled={loadingCrear}
                  onClick={() => {
                    limpiarCrearReserva();
                    setShowCrearReserva(false);
                  }}
                >
                  Cancelar
                </Button>
                {usarRangoCrear && (
                  <small style={{ color: '#64748b' }}>
                    Se intentará crear una reserva por cada día del rango seleccionado.
                  </small>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* ── MODAL DISPONIBILIDAD ───────────────────────────────────────── */}
      <Modal size="lg" show={showDisponibilidad} onHide={()=>setShowDisponibilidad(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Consultar Disponibilidad de Sala</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container text-start">
            <div className="row">
              <div className="p-2 col-md-4">
                <label className="form-label">Edificio</label>
                <select
                  className="form-select"
                  value={edificioDisp}
                  onChange={(e)=>onChangeEdificioDisponibilidad(e.target.value)}
                >
                  <option value="">Seleccione una opción</option>
                  {listaEdificios.map((e)=>(
                    <option key={e.edificio} value={e.edificio}>{e.edificio}</option>
                  ))}
                </select>
              </div>
              <div className="p-2 col-md-4">
                <label className="form-label">Sala</label>
                <select
                  className="form-select"
                  value={salaDisp}
                  onChange={(e)=>setSalaDisp(e.target.value)}
                  disabled={!edificioDisp}
                >
                  <option value="">Seleccione una opción</option>
                  {salasDisponibilidad.map((s)=>(
                    <option key={s.nombre_Sala} value={s.nombre_Sala}>{s.nombre_Sala}</option>
                  ))}
                </select>
              </div>
              <div className="p-2 col-md-4">
                <label className="form-label">Día</label>
                <input
                  type="date"
                  className="form-control"
                  value={diaDisp}
                  onChange={(e)=>setDiaDisp(e.target.value)}
                />
              </div>
            </div>

            <div className="row pt-2">
              <div className="col-md-12 d-flex gap-2">
                <Button variant="primary" onClick={consultarDisponibilidad} disabled={loadingDisp}>
                  {loadingDisp ? "Consultando..." : "Consultar"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEdificioDisp("");
                    setSalaDisp("");
                    setSalasDisponibilidad([]);
                    setDiaDisp(ymd(new Date()));
                    setResultadoDisp([]);
                    setConsultadoDisp(false);
                  }}
                >
                  Limpiar
                </Button>
              </div>
            </div>

            {consultadoDisp && (
              <div className="row pt-4">
                <div className="col-md-12">
                  {resultadoDisp.length === 0 ? (
                    <div className="alert alert-success mb-0">
                      La sala <strong>{salaDisp}</strong> en <strong>{edificioDisp}</strong> está disponible en todos los horarios del día <strong>{diaDisp}</strong>.
                    </div>
                  ) : (
                    <>
                      <div className="alert alert-warning mb-2">
                        Horarios ocupados para <strong>{salaDisp}</strong> el día <strong>{diaDisp}</strong>.
                      </div>
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm">
                          <thead>
                            <tr>
                              <th>Hora de Inicio</th>
                              <th>Hora de Finalización</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultadoDisp.map((r, i) => (
                              <tr key={i}>
                                <td>{r.hora_Inicio}</td>
                                <td>{r.hora_Fin}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* ── MODAL AGREGAR VISITANTES ────────────────────────────────────── */}
      <Modal size="lg" show={lgShow} onHide={()=>setLgShow(false)}>
        <Modal.Header closeButton><Modal.Title>Ingresar Datos de Visitantes</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="container text-start">
            {[
              [nombre1,setNombre1,identidad1,setIdentidad1],
              [nombre2,setNombre2,identidad2,setIdentidad2],
              [nombre3,setNombre3,identidad3,setIdentidad3],
              [nombre4,setNombre4,identidad4,setIdentidad4],
            ].map(([nom,setNom,id,setId],i)=>(
              <div className="row" key={i}>
                <div className="p-2 col-md-6">
                  <label className="form-label">Nombre</label>
                  <input type="text" className="form-control" value={nom}
                    placeholder="Juan López" onChange={e=>setNom(e.target.value)}/>
                </div>
                <div className="p-2 col-md-6">
                  <label className="form-label">Número de Identificación (DNI)</label>
                  <input type="text" className="form-control" value={id}
                    placeholder="####-####-#####" onChange={e=>setId(e.target.value)}/>
                </div>
              </div>
            ))}
            <div className="row pt-4">
              <Button variant="success" onClick={enviarDatos}>Guardar</Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* ── MODAL VER VISITANTES ────────────────────────────────────────── */}
      <Modal size="lg" show={lgShow2} onHide={()=>setLgShow2(false)}>
        <Modal.Header closeButton><Modal.Title>Consultar Datos de Visitantes</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="container text-start">
            <MaterialTable title="Visitas" columns={columns2} data={solicitudes5}
              icons={tableIcons} options={{search:false}}/>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Ver;